import { useState } from "react";
import * as yup from "yup";
import { Row, Col, FormGroup, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { addServerErrors } from "../../utils/react-hook-form-utils";
import { API } from "../../utils/AxiosInstance";
import { isValidationError } from "../../utils/axios-utils";
import { ServerError } from "../../types/server-error";
import { ErrorText } from "../../components/error-text";
import { AlertDialog } from "../../components/dialogs/alert-dialog";
import { useBranding } from "../../contexts/branding";
import { Spacer } from "../components/spacer";
import { PasswordField } from "../../components/password-field";
import { Spinner } from "../components/spinner";

export interface Props {
    token: string;
}

interface State {
    password: string;
    confirm_password: string;
    base?: string;
}

export function PasswordResetView({ token }: Props) {
    const [isSaving, setIsSaving] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const { t } = useTranslation();
    const { label } = useBranding();
    const schema = yup.object().shape({
        password: yup
            .string()
            .min(8, t("has to be equal or longer than 8 characters!"))
            .required(t("is required")),
        confirm_password: yup
            .string()
            .oneOf([yup.ref("password"), null], t("Passwords do not match"))
            .required(t("is required")),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm<State>({
        resolver: yupResolver(schema),
    });

    async function onSubmit(params: State) {
        try {
            setIsSaving(true);
            await API.put(`/password_resets/${token}`, params);
            setShowDialog(true);
        } catch (e) {
            if (isValidationError<ServerError>(e)) {
                addServerErrors<State>(e.data.errors, setError);
            }
            setIsSaving(false);
        }
    }

    return (
        <Row>
            <Col
                xs={12}
                md={{ offset: 2, span: 8 }}
                lg={{ offset: 2, span: 8 }}
            >
                {showDialog && (
                    <AlertDialog
                        onClose={() => {
                            window.location.href = "/";
                        }}
                    >
                        <p>{t("Password successfuly updated.")}</p>
                    </AlertDialog>
                )}
                <div className="password-reset-container">
                    <h1 className="login-header-text login-header-label">
                        {t("Create New Password")}
                    </h1>
                    <p className="subheader-slogan">
                        {t("Enter your new password to reset")}
                    </p>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <FormGroup>
                            <PasswordField
                                name="password"
                                label={t("Create Your Password")}
                                register={register}
                                errorMessage={t(
                                    errors?.password?.message || ""
                                )}
                            />
                            <i className="text-xs">
                                {t("change_password_hint")}
                            </i>
                            <br />
                            {errors.base && (
                                <ErrorText text={errors.base.message} />
                            )}
                        </FormGroup>
                        <FormGroup>
                            <PasswordField
                                name="confirm_password"
                                label={t("Enter Your Password Again")}
                                register={register}
                                errorMessage={t(
                                    errors?.confirm_password?.message || ""
                                )}
                            />
                        </FormGroup>
                        <Spacer height={12} />

                        <div>
                            <Button
                                type="submit"
                                className={
                                    label !== "HabitNu"
                                        ? "btn btn-success form-control ada-btn-success field-height"
                                        : "btn btn-success form-control field-height"
                                }
                                disabled={isSaving}
                            >
                                {t("Submit")}{" "}
                                {isSaving && <Spinner size="sm" />}
                            </Button>
                        </div>
                        <Spacer height={12} />
                    </form>
                </div>
            </Col>
        </Row>
    );
}
